/**
 * Centralized API Endpoint URLs
 * This file contains all API endpoint URLs used across the application
 */

// ==================== Auth Endpoints ====================
export const AUTH_ENDPOINTS = {
  LOGIN: '/api/auth/login',
  REFRESH_TOKEN: '/api/auth/refresh',
  USER_INFO: '/api/auth/user-info',
  USER_ROLE: '/api/auth/userrole',
  SSO_FINALIZE: '/api/auth/sso-finalize',
  LOGOUT: '/api/auth/logout',
  CHECK_ACCESS: '/api/auth/check-access',
  ACCESS_TOKEN: '/api/Auth/token',
  CHECK_AUTHENTICATION: '/api/auth/check',
};

// ==================== User Endpoints ====================
export const USER_ENDPOINTS = {
  GET_ALL_USERS: '/api/users',
  GET_USER_BY_ID: (userId: string) => `/api/users/${userId}`,
};

// ==================== User Groups Endpoints ====================
export const USER_GROUP_ENDPOINTS = {
  BASE: '/api/usergroups',
  BY_ID: (groupId: string) => `${USER_GROUP_ENDPOINTS.BASE}/${groupId}`,
};

// ==================== Project BCF-POC Endpoints ====================
export const PROJECT_ENDPOINTS = {
  BASE: '/bcf/projects',
  BCF_PROJECT_BY_ID: (projectId: string) => `${PROJECT_ENDPOINTS.BASE}/${projectId}`,
  TOP_PANEL_INFO: (projectId: number) => `/api/project/${projectId}/top-panel-info`,
  PROFESSION_ROLE: '/api/profession-role',
  DATASETS: '/api/document/manage/datasets',
  CHECK_DOCUMENT_NAME_EXISTS: '/api/document/manage/check-document-name-exists',
  GET_PROJECT_DETAIL: (projectId: number) => `/api/project/${projectId}`,
  GROUP_SETTING_PROJECT_REQUEST: (projectId: number) => `/api/request/group-setting/project/${projectId}/init`,
};

// ==================== Building Endpoints ====================
export const BUILDING_ENDPOINTS = {
  BASE: '/api/buildings',
  BY_PROJECT_ID: (projectId: string) => `${BUILDING_ENDPOINTS.BASE}/project/${projectId}`,
  BY_ID: (buildingId: string) => `${BUILDING_ENDPOINTS.BASE}/${buildingId}`,
};

// ==================== Publication Endpoints ====================
export const PUBLICATION_ENDPOINTS = {
  SHARE: '/api/design/share/sharing',
  INIT_SC002: '/api/design/share/init',
};

// ==================== Document Endpoints ====================
export const DOCUMENT_ENDPOINTS = {
  INIT_SC007: '/api/document/manage/init',
  DOWNLOAD_FILE: (documentId: string) => `/api/document/${documentId}/download`,
  IMPORT_DOCUMENTS: '/api/document/manage/import-document',
  UPDATE_DOCUMENT_METADATA: '/api/document/manage/document-metadata',
  DELETE_DOCUMENT: '/api/document/manage/delete',
  AUTOMATION_CHECK_RESULT_BY_ID: (documentId: number) => `/api/document/manage/${documentId}/automation-check-result`,
  BOX_ACCESS_TOKEN: '/api/document/manage/box-access-token',
  EXPORT_AUTOMATION_CHECK_RESULT: (documentId: number) =>
    `/api/document/manage/${documentId}/export-automation-check-result`,
};

// ==================== Proposal Share Status Endpoints ====================
export const PROPOSAL_SHARE_STATUS_ENDPOINTS = {
  INIT_SC003: '/api/proposal-share-status/init',
  GET_AUTOMATION_CHECK_RESULT: '/api/proposal-share-status/get-auto-result',
  GET_AUTOMATION_CHECK_RESULT_DETAIL: '/api/proposal-share-status/get-auto-result-detail',
  EXPORT_FILE_AUTOMATION_RESULT: '/api/proposal-share-status/export-auto-result',
  APPROVAL_AUTOMATION_RESULT: '/api/proposal-share-status/bypass-auto-result',
};

// ==================== Plan Endpoints ====================
export const PLAN_ENDPOINTS = {
  BASE: '/api/plans',
  BY_ID: (planId: string) => `${PLAN_ENDPOINTS.BASE}/${planId}`,
};

// ==================== Topic Endpoints ====================
export const TOPIC_ENDPOINTS = {
  BASE: '/bcf/topics',
  BY_PROJECT_ID: (projectId: string) => `${TOPIC_ENDPOINTS.BASE}/${projectId}`,
  BY_TOPIC_GUID: (topicGuid: string) => `${TOPIC_ENDPOINTS.BASE}/topic/${topicGuid}`,
  BY_ID: (topicGuid: string) => `${TOPIC_ENDPOINTS.BASE}/${topicGuid}`,
  WITH_IMAGES: '/bcf/topics/with-images',
  WITH_FILES: '/bcf/topics/with-files',
  EXPORT_EXCEL: (projectId: string) => `${TOPIC_ENDPOINTS.BASE}/export-excel/${projectId}`,
  EXPORT_BCF: (projectId: string) => `${TOPIC_ENDPOINTS.BASE}/download-bcf/${projectId}`,
  UPLOAD_IMAGE: '/bcf/topics/upload/image',
  UPLOAD_FILE: '/bcf/topics/upload/file',
  DELETE_FILE: '/bcf/topics/upload/delete',
  BUILDING_DOCUMENTS: (buildingId: string) => `${TOPIC_ENDPOINTS.BASE}/building/${buildingId}/documents`,
};

// ==================== Comment Endpoints ====================
export const COMMENT_ENDPOINTS = {
  BY_TOPIC: (topicGuid: string) => `${TOPIC_ENDPOINTS.BASE}/${topicGuid}/comments`,
  BY_ID: (topicGuid: string, guid: string) => `${TOPIC_ENDPOINTS.BASE}/${topicGuid}/comments/${guid}`,
};

// ==================== Object Endpoints ====================
export const OBJECT_ENDPOINTS = {
  BASE: '/api/models/objects',
  BY_GUID: (guid: string) => `${OBJECT_ENDPOINTS.BASE}/${encodeURIComponent(guid)}`,
  BY_NAME: (name: string) => `${OBJECT_ENDPOINTS.BASE}/find-by-name/${encodeURIComponent(name)}`,
  BY_MODELS: '/api/models/objects/by-models',
  MODEL_VERSIONS: (ifcProjectId: string) => `/api/models/versions/${encodeURIComponent(ifcProjectId)}`,
  HISTORY: (guid: string) => `${OBJECT_ENDPOINTS.BASE}/history/${encodeURIComponent(guid)}`,
  SEARCH: '/api/models/objects/search',
  SPLIT: '/api/models/objects/split',
  BULK_UPDATE: (updatedBy: string) => `${OBJECT_ENDPOINTS.BASE}/bulk-update?updatedBy=${updatedBy}`,
};

// ==================== MasterCode Endpoints ====================
export const MASTER_CODE_ENDPOINTS = {
  BY_CODE_TYPE: '/api/master-code',
};

// ==================== Project Management Endpoints ====================
export const PROJECT_MANAGEMENT_ENDPOINTS = {
  SEARCH_PROJECTS: '/api/project/manage',
  SEARCH_PROJECT_MEMBERS: '/api/project/member/search',
  PROJECT_ROLES: '/api/project/role',
  ADD_PROJECT_MEMBER: '/api/project/member/add',
  INIT_PROJECT_MEMBER: '/api/project/member/init',
  CHANGE_PROJECT_MEMBER_ROLE: '/api/project/member/update-project-member',
};
// ==================== Users Management Endpoints ====================
export const USERS_MANAGEMENT_ENDPOINTS = {
  SEARCH_USERS: '/api/user/manage',
  GET_USER_BY_ID: '/api/user',
  DISABLE_ACCOUNT: '/api/user/disable-account',
  REACTIVATE_ACCOUNT: '/api/user/reactivate-account',
  DELETE_ACCOUNT: '/api/user/delete',
  UPDATE_ROLE: '/api/user/role',
  ADD_INTERNAL_MEMBER: '/api/user/manage/member/add-internal',
  ADD_EXTERNAL_MEMBER: '/api/user/manage/member/add-external',
  EDIT_USER_BY_ID: '/api/user/manage/edit',
};

// ==================== System Role  Endpoints ====================
export const SYSTEM_ROLE_ENDPOINTS = {
  SYSTEM_ROLES: '/api/system-role',
};

// ==================== Employee Endpoints ====================
export const EMPLOYEE_ENDPOINTS = {
  GET_EMPLOYEE_CONCURRENT: '/api/employee-concurrent',
};

// ==================== SC012 Endpoints ====================
export const SC012_ENDPOINTS = {
  // API-SC012-01: GET init screen info with requestId path param
  INIT: (requestId: number) => `/api/request/view/${requestId}/init`,
  // API-SC012-02: Add thread chat (POST with JSON body)
  ADD_THREAD_CHAT: '/api/request/view/add-thread-chat',
  // API-SC012-03:  GET with requestId path param
  DOWNLOAD_ALL_CHAT_DOCUMENT: (requestId: number) => `/api/request/${requestId}/download-all-chat-document`,
  // API-SC012-04: GET with requestId path param
  LIST_THREAD_CHAT: (requestId: number) => `/api/request/view/${requestId}/list-thread-chat`,
  // API-SC012-05: no /view/ prefix per DD
  REPLY_THREAD_CHAT: '/api/request/view/reply-thread-chat',
  // API-SC012-06: GET with requestId path param
  GROUP_MEMBER_LIST: '/api/request/view/group-member-list',
  // API-SC012-07: Update progress for subtask assignee
  ASSIGNEE_UPDATE_PROGRESS: '/api/request/view/assignee-update-progress',
  // API-SC012-08: Update deadline for subtask assignee
  ASSIGNEE_UPDATE_DEADLINE: '/api/request/view/assignee-update-deadline',
  // API-SC012-09:
  DLM_INTEGRATION: '/api/request/view/dlm-linkage',
  // API-SC012-18: Validate data before DLM linkage
  VALIDATE_DLM_LINKAGE: '/api/request/view/dlm-linkage/validate',
  // API-SC012-10: GET with requestId path param
  GET_CHAT_ATTACHMENTS_FILE_LIST: (requestId: number) => `/api/request/view/${requestId}/chat-attachments-file-list`,
  // API-SC012-11: GET with qaId path param
  LIST_THREAD_COMMENT: (qaId: number) => `/api/request/view/${qaId}/list-thread-comment`,
  // API-SC012-12: list-user-mention per DD
  USER_MENTION: '/api/request/view/list-user-mention',
  // API-SC012-13: Edit thread comment (POST with JSON body)
  EDIT_THREAD_COMMENT: '/api/request/view/edit-thread-comment',
  // API-SC012-14: Upload deliverable files (multipart/form-data)
  UPLOAD_DELIVERABLE: '/api/request/view/upload-deliverable',
  // API-SC012-15: Delete deliverable file
  DELETE_DELIVERABLE_FILE: '/api/request/view/delete-deliverable-file',
  // API-SC012-16: Get deliverable file list by requestId (path param)
  DELIVERABLE_FILES_LIST: (requestId: number) => `/api/request/view/${requestId}/deliverable-files-list`,
  // API-SC012-17:
  WORK_REQUEST: '/api/request/view/work-request',
  // API-SC012-19: Init estimate info for 数量積算・値入エリア
  INIT_ESTIMATE_INFO: '/api/request/view/init-estimate-info',
  DOWNLOAD_DOCUMENT: (boxFileId: string) => `/api/document/${boxFileId}/download`,
};

// ==================== Request - SC005 ====================
export const REQUEST_ENDPOINTS = {
  REQUEST_INIT: '/api/request/load-requests',
  REQUEST_DETAIL: (projectId: number, requestId: number) => `/api/request/detail/${projectId}/${requestId}`,
  REQUEST_ADD: '/api/request/add',
  REQUEST_EDIT: '/api/request/edit',
  REQUEST_DELETE: '/api/request/delete',
  REQUEST_UPDATE: '/api/request/update',
  SEARCH_PROJECT_MEMBER: '/api/request/group-setting/search-project-member',
  SEARCH_ESTIMATE_GROUP_PROJECT_MEMBER: '/api/request/group-setting/search-estimate-group-project-member',
  UPDATE_MEMBER: '/api/request/group-setting/update',
};

// ==================== Proposal Management Endpoints ====================
export const PROPOSAL_MANAGEMENT_ENDPOINTS = {
  PROPOSAL_LIST: '/api/proposal-manage/init',
  DOWNLOAD_DIALOG_INIT: '/api/proposal-manage/download-dialog/init',
  DATASET_DELETE: '/api/proposal-manage/dataset/delete',
  PUBLICATION_COMMENT_EDIT: '/api/proposal-manage/publication/comment',
  FILE_MANAGEMENT_INIT_PROJECT_LIST: '/api/proposal-manage/file-management/init-project-list',
  COPY_DATASET_DOCS: '/api/proposal-manage/dataset-docs/copy',
  DELETE_DATASET_DOCS: '/api/proposal-manage/dataset-docs/delete',
  INIT_DATASET_LIST: '/api/proposal-manage/file-management/init-dataset-list',
};

// ==================== Project BIM Endpoints ====================
export const PROJECT_BIM_ENDPOINTS = {
  BASE: '/api/project',
  PROJECT_DETAIL_BY_ID: (projectId: number) => `${PROJECT_BIM_ENDPOINTS.BASE}/detail/${projectId}`,
};

// ==================== Dataset Endpoints ====================
export const DATASET_ENDPOINTS = {
  DATASET_DOWNLOAD: (datasetId: number) => `/api/dataset/${datasetId}/download`,
};

// ==================== APS Endpoints ====================
export const APS_ENDPOINTS = {
  GET_CONFIG: '/api/aps/config',
};

// ==================== IFC Endpoints ====================
export const IFC_ENDPOINTS = {
  IFC_DOWNLOAD: (ifcFileId: number) => `/api/ifc/${ifcFileId}/download`,
};

// ==================== BV001 Endpoints ====================
export const BV001_ENDPOINTS = {
  API_BV001_01: `/api/viewer/design/datasets`,
  API_BV001_02: `/api/viewer/design/publications`,
};

// ==================== BV002 Endpoints ====================
export const BV002_ENDPOINTS = {
  API_BV002_02: `/api/viewer/design/get-access-token`,
  API_BV002_02_04: `/api/viewer/design/list-floors`,
  API_BV002_03: `/api/viewer/design/get-model-display-info`,
  API_BV002_04: `/api/viewer/design/get-list-dataset`,
  API_BV002_D001_01: `/api/viewer/design/get-list-cost-estimate-component`,
  API_BV002_D001_02: `/api/viewer/design/update-cost-estimate-component`,
  API_BV002_D001_03: `/api/viewer/design/split-cost-estimate-component`,
  API_BV002_D001_04: `/api/viewer/design/revert-cost-estimate-component`,
  API_BV002_D002_01: `/api/viewer/design/get-list-duplicate-component`,
  API_BV002_D002_02: `/api/viewer/design/combine-duplicate-component`,
  API_BV002_D002_03: `/api/viewer/design/search-duplicate-component`,
  API_BV002_D002_04: `/api/viewer/design/confirm-component-info`,
  API_BV002_D003_01: `/api/viewer/design/get-component-info`,
  API_BV002_D003_02: `/api/viewer/design/list-dataset-documents`,
  API_BV002_D003_03: `/api/viewer/design/list-dataset-users`,
  API_BV002_D003_04: `/api/viewer/design/create-topic`,
  API_BV002_D004_01: `/api/viewer/design/search-topic`,
  API_BV002_D004_02: `/api/viewer/design/get-detail-topic`,
  API_BV002_D004_03: `/api/viewer/design/topic-export`,
  API_BV002_D004_04: `/api/viewer/design/get-topic-thumbnail`,
  API_BV002_D004_05: `/api/viewer/design/delete-topic`,
  API_BV002_D004_06: `/api/viewer/design/get-list-topic-attachment`,
  API_BV002_D004_07: `/api/viewer/design/create-reply-topic`,
  API_BV002_D004_08: `/api/viewer/design/update-topic`,
  API_BV002_D004_09: `/api/viewer/design/check-document-name-exists`,
  API_BV002_D005_01: `/api/viewer/design/list-topic-documents`,
  API_BV002_D005_02: `/api/viewer/design/preview-topic-document`,
  API_BV002_D006_01: `/api/viewer/design/get-list-detail-revision`,
  API_BV002_D007_01: `/api/viewer/design/automation-check-result`,
};

// ==================== Dashboard - SC004 ====================
export const DASHBOARD_ENDPOINTS = {
  INIT_OVERVIEW: '/api/dashboard/init-overview',
  INIT_HISTORY: '/api/dashboard/history-overview',
  INIT_PROJECT_DETAIL: (projectId: number) => `/api/dashboard/project/${projectId}`,
  INIT_HISTORY_PROJECT_DETAIL: (projectId: number) => `/api/dashboard/project/${projectId}/history-project`,
};

// ==================== Project Selection - CM004 ====================
export const PROJECT_SELECTION_ENDPOINTS = {
  SEARCH_PROJECT: '/api/project/search',
  CONFIRMED_MESSAGE_LIST: '/api/notifications/confirmed-message-list',
  UNREAD_MESSAGE_LIST: '/api/notifications/unread-message-list',
  READ_ALL_MESSAGES: '/api/notifications/read-all',
  UPDATE_READ_STATUS: '/api/notifications/update-read-status',
  UPDATE_CONFIRM_STATUS: '/api/notifications/update-confirm-status',
};

// ==================== Common - SC002 & SC003 ====================
export const COMMON_ENDPOINTS = {
  GET_BIM_PROJECT_ID: (designPortalProjectId: number) => `/api/project/${designPortalProjectId}/bim-id`,
};
